import { useState, useEffect, useRef } from "react";
import { useRive, Layout, Fit, Alignment } from "@rive-app/react-canvas";
import styled, { css } from "styled-components";

interface RiveWrapperProps {
  stateMachine: string;
  artboard?: string;
  riveFilePath: string;
  onRiveInit?: (rive: any) => void;
}

const RiveWrapper = ({ stateMachine, artboard, riveFilePath, onRiveInit }: RiveWrapperProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const riveContainerRef = useRef<HTMLDivElement>(null);

  const { rive, RiveComponent } = useRive({
    src: riveFilePath,
    stateMachines: stateMachine,
    artboard: artboard,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
    onLoad: () => {
      rive?.resizeDrawingSurfaceToCanvas();
      if (onRiveInit && rive) {
        onRiveInit(rive);
      }
      setTimeout(() => setIsLoaded(true), 100);
    },
  });

  useEffect(() => {
    if (rive && onRiveInit) {
      const inputs = rive.stateMachineInputs?.(stateMachine) || null;
      onRiveInit({
        rive,
        inputs,
        fire: (eventName: string) => {
          try {
            if (!inputs) return;
            const trigger = inputs.find((input: any) => input.name === eventName);
            if (trigger) {
              trigger.fire();
            }
          } catch (error) {
            console.error(`Failed to fire event "${eventName}":`, error);
          }
        },
      });
    }
  }, [rive, onRiveInit, stateMachine]);

  const resizeRiveCanvas = () => {
    if (!rive || !riveContainerRef.current) return;
    const container = riveContainerRef.current;

    const canvas = container.querySelector("canvas");
    if (!canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;
    canvas.width = Math.floor(containerWidth * pixelRatio);
    canvas.height = Math.floor(containerHeight * pixelRatio);

    // Scale the drawing context
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(pixelRatio, pixelRatio);
    }

    // Tell Rive to resize
    rive.resizeDrawingSurfaceToCanvas();
    rive.startRendering();
  };

  return (
    <RiveContainer
      ref={riveContainerRef}
      onTransitionEnd={resizeRiveCanvas}
      style={{ cursor: "pointer" }}
      $isLoaded={isLoaded}
    >
      <RiveComponent />
    </RiveContainer>
  );
};

export default RiveWrapper;

const RiveContainer = styled.div<{ $isLoaded: boolean }>`
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.8s ease-out, transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 24px;
  ${props =>
    props.$isLoaded &&
    css`
      opacity: 1;
      transform: scale(1);
    `}
`;
